import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from "@hello-pangea/dnd";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Drawer,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  InputLabel,
  Switch,
  Slider,
  TextField,
  IconButton
} from "@mui/material";
import { ProgressButton } from "@qubit/autoparts";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  BOARD_DIMENSION_MARKS,
  getRestrictedDimensionSliderSx
} from "~/features/andon/andon.styles";
import {
  ANDON_DISPLAY_LINE_ORDER,
  AndonDisplayLine
} from "~/features/andon/andon.types";
import { getAndonDisplayLineLabel } from "~/features/andon/andon.utils";

import {
  clampBoardDimension,
  MAX_BOARD_DIMENSION,
  MIN_BOARD_DIMENSION,
  normalizeBoardDimensionFloor
} from "~/features/andon/andonBoard/andonBoardLayoutEditor.utils";

type LayoutEditorDrawerProps = {
  open: boolean;
  onClose: () => void;
  columns: number;
  rows: number;
  minColumns?: number;
  minRows?: number;
  scale: number;
  iconScale: number;
  displayLines: AndonDisplayLine[];
  layoutName?: string | null;
  showPrivateCheckbox?: boolean;
  isPrivate?: boolean;
  onColumnsChange: (columns: number) => void;
  onRowsChange: (rows: number) => void;
  onScaleChange: (scale: number) => void;
  onIconScaleChange: (scale: number) => void;
  onDisplayLinesChange: (lines: AndonDisplayLine[]) => void;
  onLayoutNameChange?: (name: string) => void;
  onPrivateChange?: (isPrivate: boolean) => void;
};

type LineItemProps = {
  line: AndonDisplayLine;
  index: number;
  enabled: boolean;
  onToggle: (line: AndonDisplayLine) => void;
};

function LineItem({
  line,
  index,
  enabled,
  onToggle,
  label
}: LineItemProps & { label: string }) {
  return (
    <Draggable draggableId={line} index={index}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            padding: 0.75,
            backgroundColor: snapshot.isDragging
              ? "action.hover"
              : "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            mb: 0.5,
            opacity: enabled ? 1 : 0.5
          }}
        >
          <DragIndicatorIcon sx={{ cursor: "grab" }} />
          <Typography
            sx={{ flex: 1, textDecoration: enabled ? "none" : "line-through" }}
          >
            {label}
          </Typography>
          <Switch
            checked={enabled}
            onChange={() => onToggle(line)}
            size="small"
          />
        </Box>
      )}
    </Draggable>
  );
}

export function LayoutEditorDrawer({
  open,
  onClose,
  columns,
  rows,
  minColumns = 1,
  minRows = 1,
  scale,
  iconScale,
  displayLines,
  layoutName,
  showPrivateCheckbox = false,
  isPrivate = false,
  onColumnsChange,
  onRowsChange,
  onScaleChange,
  onIconScaleChange,
  onDisplayLinesChange,
  onLayoutNameChange,
  onPrivateChange
}: LayoutEditorDrawerProps) {
  const { t } = useTranslation();

  const columnsFloor = normalizeBoardDimensionFloor(minColumns);
  const rowsFloor = normalizeBoardDimensionFloor(minRows);

  const [localColumns, setLocalColumns] = useState(columns);
  const [localRows, setLocalRows] = useState(rows);
  const [localScale, setLocalScale] = useState<number>(scale || 1.0);
  const [localIconScale, setLocalIconScale] = useState<number>(
    iconScale || 1.0
  );

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setLocalColumns(clampBoardDimension(columns, columnsFloor));
  }, [columns, columnsFloor]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setLocalRows(clampBoardDimension(rows, rowsFloor));
  }, [rows, rowsFloor]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setLocalScale(scale || 1.0);
  }, [scale]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setLocalIconScale(iconScale || 1.0);
  }, [iconScale]);

  const allLines: AndonDisplayLine[] = ANDON_DISPLAY_LINE_ORDER;

  const reorder = (
    list: AndonDisplayLine[],
    startIndex: number,
    endIndex: number
  ) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newOrder = reorder(
      allLines,
      result.source.index,
      result.destination.index
    );
    const newDisplayLines = newOrder.filter((line) =>
      displayLines.includes(line)
    );
    onDisplayLinesChange(newDisplayLines);
  };

  const handleToggleLine = (line: AndonDisplayLine) => {
    if (displayLines.includes(line)) {
      onDisplayLinesChange(displayLines.filter((l) => l !== line));
    } else {
      onDisplayLinesChange([...displayLines, line]);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        role: "dialog",
        "aria-label": t("settings"),
        sx: { width: { xs: "100%", sm: 400 } }
      }}
    >
      <Box display="flex" flexDirection="column" height="100%">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1 }}
        >
          <Typography variant="h6">{t("settings")}</Typography>
          <IconButton onClick={onClose} aria-label={t("close")} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gap={1.5}
          sx={{ px: 2, pb: 2, pt: 0.5, flex: 1, overflowY: "auto" }}
        >
          {layoutName && onLayoutNameChange && (
            <Box>
              <InputLabel htmlFor="layout-name">
                <Typography variant="button">{t("layout name")}</Typography>
              </InputLabel>
              <TextField
                id="layout-name"
                fullWidth
                size="small"
                value={layoutName}
                onChange={(e) => onLayoutNameChange(e.target.value)}
              />
            </Box>
          )}

          {showPrivateCheckbox && onPrivateChange && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPrivate}
                  onChange={(event) => onPrivateChange(event.target.checked)}
                  size="small"
                />
              }
              label={t("private")}
            />
          )}

          <Box>
            <InputLabel htmlFor="editor-columns">
              <Typography variant="button">
                {t("columns")}: {localColumns}
              </Typography>
            </InputLabel>
            <Box sx={{ px: 1, pt: 0.5 }}>
              <Slider
                id="editor-columns"
                aria-label={t("columns")}
                value={localColumns}
                min={MIN_BOARD_DIMENSION}
                max={MAX_BOARD_DIMENSION}
                step={1}
                marks={BOARD_DIMENSION_MARKS}
                valueLabelDisplay="auto"
                sx={getRestrictedDimensionSliderSx(columnsFloor)}
                onChange={(_e, value) => {
                  if (typeof value === "number") {
                    setLocalColumns(clampBoardDimension(value, columnsFloor));
                  }
                }}
                onChangeCommitted={(_e, value) => {
                  if (typeof value === "number") {
                    onColumnsChange(clampBoardDimension(value, columnsFloor));
                  }
                }}
              />
            </Box>
          </Box>

          <Box>
            <InputLabel htmlFor="editor-rows">
              <Typography variant="button">
                {t("rows")}: {localRows}
              </Typography>
            </InputLabel>
            <Box sx={{ px: 1, pt: 0.5 }}>
              <Slider
                id="editor-rows"
                aria-label={t("rows")}
                value={localRows}
                min={MIN_BOARD_DIMENSION}
                max={MAX_BOARD_DIMENSION}
                step={1}
                marks={BOARD_DIMENSION_MARKS}
                valueLabelDisplay="auto"
                sx={getRestrictedDimensionSliderSx(rowsFloor)}
                onChange={(_e, value) => {
                  if (typeof value === "number") {
                    setLocalRows(clampBoardDimension(value, rowsFloor));
                  }
                }}
                onChangeCommitted={(_e, value) => {
                  if (typeof value === "number") {
                    onRowsChange(clampBoardDimension(value, rowsFloor));
                  }
                }}
              />
            </Box>
          </Box>

          <Box>
            <InputLabel htmlFor="editor-scale">
              <Typography variant="button">
                {t("font scale")}: {localScale.toFixed(2)}x
              </Typography>
            </InputLabel>
            <Box sx={{ px: 1, pt: 0.5 }}>
              <Slider
                id="editor-scale"
                aria-label={t("font scale")}
                value={localScale}
                min={0.5}
                max={2.0}
                step={0.05}
                marks={[
                  { value: 0.5, label: "0.5x" },
                  { value: 0.75, label: "0.75x" },
                  { value: 1.0, label: "1.0x" },
                  { value: 1.25, label: "1.25x" },
                  { value: 1.5, label: "1.5x" },
                  { value: 2.0, label: "2.0x" }
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value.toFixed(2)}x`}
                onChange={(_e, value) => {
                  if (typeof value === "number") {
                    setLocalScale(value);
                  }
                }}
                onChangeCommitted={(_e, value) => {
                  if (typeof value === "number") {
                    onScaleChange(value);
                  }
                }}
              />
            </Box>
          </Box>

          <Box>
            <InputLabel htmlFor="editor-icon-scale">
              <Typography variant="button">
                {t("icon scale")}: {localIconScale.toFixed(2)}x
              </Typography>
            </InputLabel>
            <Box sx={{ px: 1, pt: 0.5 }}>
              <Slider
                id="editor-icon-scale"
                aria-label={t("icon scale")}
                value={localIconScale}
                min={0.5}
                max={3.0}
                step={0.1}
                marks={[
                  { value: 0.5, label: "0.5x" },
                  { value: 1.0, label: "1.0x" },
                  { value: 1.5, label: "1.5x" },
                  { value: 2.0, label: "2.0x" },
                  { value: 3.0, label: "3.0x" }
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value.toFixed(2)}x`}
                onChange={(_e, value) => {
                  if (typeof value === "number") {
                    setLocalIconScale(value);
                  }
                }}
                onChangeCommitted={(_e, value) => {
                  if (typeof value === "number") {
                    onIconScaleChange(value);
                  }
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="button" sx={{ display: "block", mb: 0.5 }}>
              {t("display lines")}
            </Typography>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="display-lines">
                {(provided) => (
                  <Box {...provided.droppableProps} ref={provided.innerRef}>
                    {allLines.map((line, index) => (
                      <LineItem
                        key={line}
                        line={line}
                        index={index}
                        label={getAndonDisplayLineLabel(line, t)}
                        enabled={displayLines.includes(line)}
                        onToggle={handleToggleLine}
                      />
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "flex-end"
          }}
        >
          <ProgressButton onClick={onClose}>{t("close")}</ProgressButton>
        </Box>
      </Box>
    </Drawer>
  );
}
